<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title">
        이벤트 이력
        <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
      </div>

      <div class="header-buttons">
        <button class="btn secondary" @click="onClickGenerate" :disabled="generating">
          {{ generating ? '생성 중...' : '이벤트 생성' }}
        </button>

        <button class="btn danger" @click="onDeleteByCondition" :disabled="deletingByCondition || loading">
          {{ deletingByCondition ? '삭제 중...' : '조건 삭제' }}
        </button>

        <button class="btn primary" @click="fetchEventHistoryList" :disabled="loading">
          {{ loading ? '조회 중...' : '새로고침' }}
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="filter-section">
        <div class="filter-grid">
          <div class="filter-item">
            <label class="filter-label">시작일시</label>
            <input
              v-model="filters.startAt"
              type="datetime-local"
              class="filter-input"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">종료일시</label>
            <input
              v-model="filters.endAt"
              type="datetime-local"
              class="filter-input"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">현장 선택</label>
            <select v-model="filters.siteId" class="filter-input" @change="onChangeSite">
              <option :value="null">전체</option>
              <option
                v-for="site in siteOptions"
                :key="site.id"
                :value="site.id"
              >
                {{ site.name }}
              </option>
            </select>
          </div>

          <div class="filter-item">
            <label class="filter-label">이벤트 유형</label>
            <select v-model="filters.eventType" class="filter-input">
              <option value="">전체</option>
              <option value="경고">경고</option>
              <option value="감지">감지</option>
              <option value="경보">경보</option>
            </select>
          </div>

          <div class="filter-item">
            <label class="filter-label">로거명</label>
            <input
              v-model.trim="filters.loggerName"
              type="text"
              class="filter-input"
              placeholder="로거명 검색"
              @keyup.enter="onSearch"
            />
          </div>

          <div class="filter-item filter-actions">
            <button class="btn primary" @click="onSearch" :disabled="loading">
              검색
            </button>
            <button class="btn ghost" @click="onResetFilters" :disabled="loading">
              초기화
            </button>
          </div>
        </div>
      </div>

      <div class="table-section">
        <table v-if="tableRows.length" class="data-table">
          <colgroup>
            <col class="col-toggle" />
            <col class="col-date" />
            <col class="col-site" />
            <col class="col-logger" />
            <col class="col-sensor" />
            <col class="col-location" />
            <col class="col-type" />
            <col class="col-number" />
            <col class="col-number" />
            <col class="col-status" />
            <col class="col-action" />
          </colgroup>

          <thead>
            <tr>
              <th></th>
              <th>발생일시</th>
              <th>현장명</th>
              <th>로거명</th>
              <th>센서명</th>
              <th>설치위치</th>
              <th>이벤트유형</th>
              <th>현재값</th>
              <th>임계값</th>
              <th>상태</th>
              <th>삭제</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="row in tableRows" :key="row.id">
              <tr
                class="table-row"
                :class="{ expanded: row.expanded }"
                @click="toggleRow(row.id)"
              >
                <td class="td-toggle">
                  <button
                    class="expand-btn"
                    type="button"
                    @click.stop="toggleRow(row.id)"
                  >
                    {{ row.expanded ? '－' : '+' }}
                  </button>
                </td>

                <td>{{ formatDateTime(row.occurredAt) }}</td>

                <td class="td-ellipsis" :title="row.siteName || '-'">
                  {{ row.siteName || '-' }}
                </td>

                <td class="td-ellipsis" :title="resolveLoggerName(row)">
                  {{ resolveLoggerName(row) }}
                </td>

                <td class="td-ellipsis" :title="row.sensorName || '-'">
                  {{ row.sensorName || '-' }}
                </td>

                <td class="td-ellipsis" :title="row.location || row.locationDesc || '-'">
                  {{ row.location || row.locationDesc || '-' }}
                </td>

                <td>
                  <span class="event-pill" :class="getEventTypeClass(row.eventType)">
                    {{ row.eventType || '-' }}
                  </span>
                </td>

                <td>{{ formatNumber(row.currentValue) }}</td>
                <td>{{ formatNumber(row.thresholdValue) }}</td>

                <td class="td-ellipsis" :title="row.statusText || '-'">
                  {{ row.statusText || '-' }}
                </td>

                <td @click.stop>
                  <button
                    class="btn xs danger"
                    @click="onDeleteRow(row)"
                    :disabled="deletingRowId === row.id"
                  >
                    {{ deletingRowId === row.id ? '삭제중' : '삭제' }}
                  </button>
                </td>
              </tr>

              <tr v-if="row.expanded" class="detail-row">
                <td colspan="11" class="detail-cell">
                  <div class="detail-panel">
                    <div class="detail-left">
                      <div class="detail-title">이벤트 상세 정보</div>

                      <div class="detail-grid">
                        <div class="detail-label">회사명</div>
                        <div class="detail-value">{{ resolveCompanyName(row) }}</div>

                        <div class="detail-label">현장명</div>
                        <div class="detail-value">{{ row.siteName || '-' }}</div>

                        <div class="detail-label">발생일시</div>
                        <div class="detail-value">{{ formatDateTime(row.occurredAt) }}</div>

                        <div class="detail-label">이벤트 유형</div>
                        <div class="detail-value">{{ row.eventType || '-' }}</div>

                        <div class="detail-label">기준 단계</div>
                        <div class="detail-value">{{ row.thresholdLevel || '-' }}</div>

                        <div class="detail-label">로거명</div>
                        <div class="detail-value">{{ resolveLoggerName(row) }}</div>

                        <div class="detail-label">센서명</div>
                        <div class="detail-value">{{ row.sensorName || '-' }}</div>

                        <div class="detail-label">발생위치</div>
                        <div class="detail-value">{{ row.location || row.locationDesc || '-' }}</div>

                        <div class="detail-label">이상 상태</div>
                        <div class="detail-value">{{ row.statusText || '-' }}</div>

                        <div class="detail-label">현재 값</div>
                        <div class="detail-value is-emphasis">
                          {{ formatDetailValue(row.currentValue, inferUnit(row)) }}
                        </div>

                        <div class="detail-label">임계 값</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.thresholdValue, inferUnit(row)) }}
                        </div>

                        <div class="detail-label">측정 ID</div>
                        <div class="detail-value">{{ row.measurementId ?? '-' }}</div>

                        <div class="detail-label">측정량</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.measureAmount, null) }}
                        </div>

                        <div class="detail-label">변화량(deg)</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.measureDeg, 'deg') }}
                        </div>

                        <div class="detail-label">변위량(mm)</div>
                        <div class="detail-value is-strong">
                          {{ formatDetailValue(row.measureMm, 'mm') }}
                        </div>

                        <div class="detail-label">초기 측정량</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.baseAmount, null) }}
                        </div>

                        <div class="detail-label">초기 변화량(deg)</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.baseDeg, 'deg') }}
                        </div>

                        <div class="detail-label">초기 변위량(mm)</div>
                        <div class="detail-value">
                          {{ formatDetailValue(row.baseMm, 'mm') }}
                        </div>
                      </div>
                    </div>

                    <div class="detail-right">
                      <div class="detail-map-title">발생 위치 상세 보기</div>

                      <div class="detail-map-box">
                        <template v-if="row.drawingImageUrl">
                          <div
                            v-if="row.drawingRenderWidth > 0 && row.drawingRenderHeight > 0"
                            class="detail-map-stage"
                          >
                            <div
                              class="detail-map-image-wrap"
                              :style="getDrawingImageBoxStyle(row)"
                            >
                              <img
                                :src="row.drawingImageUrl"
                                alt="event map"
                                class="detail-map-image"
                                @load="onDrawingImageLoad(row.id, $event)"
                                draggable="false"
                              />

                              <div
                                v-for="logger in getEventTargetLoggers(row)"
                                :key="getDrawingLoggerKey(logger)"
                                class="logger-marker"
                                :style="getLoggerMarkerStyle(row, logger)"
                              >
                                <div
                                  class="logger-icon-wrap"
                                  :class="getLoggerStatusClass(row, logger)"
                                >
                                  <img
                                    :src="getLoggerIconSrc(row, logger)"
                                    :alt="getLoggerStatusLabel(row, logger)"
                                    class="logger-status-icon"
                                  />

                                  <div class="logger-tooltip">
                                    <div class="tooltip-row">
                                      <span class="tooltip-label">장비명 :</span>
                                      <span class="tooltip-value">
                                        {{ resolveDrawingLoggerName(logger) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">상태 :</span>
                                      <span class="tooltip-value">
                                        {{ getLoggerStatusLabel(row, logger) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">측정량 :</span>
                                      <span class="tooltip-value">
                                        {{ formatNumber(row.measureAmount) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">변화량 :</span>
                                      <span class="tooltip-value">
                                        {{ formatNumber(row.measureDeg) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">변위량 :</span>
                                      <span class="tooltip-value">
                                        {{ formatNumber(row.measureMm) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">임계값 :</span>
                                      <span class="tooltip-value">
                                        {{ formatNumber(row.thresholdValue) }}
                                      </span>
                                    </div>

                                    <div class="tooltip-row">
                                      <span class="tooltip-label">기준 단계 :</span>
                                      <span class="tooltip-value">
                                        {{ row.thresholdLevel || '-' }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <img
                            v-else
                            :src="row.drawingImageUrl"
                            alt="event map measure"
                            class="detail-map-image detail-map-measure-image"
                            @load="onDrawingImageLoad(row.id, $event)"
                          />
                        </template>

                        <div v-else class="detail-map-empty">
                          이미지 없음
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-else class="table-empty">
          {{ loading ? '이벤트 이력을 불러오는 중입니다.' : '표시할 이벤트 이력이 없습니다' }}
        </div>
      </div>

      <div class="pagination-area">
        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="onChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

import iconDangerous from '@/assets/sensor/sensor-status-dangerous.png'
import iconDetect from '@/assets/sensor/sensor-status-detect.png'
import iconWarning from '@/assets/sensor/sensor-status-warning.png'
import iconNormal from '@/assets/sensor/sensor-status-normal.png'

export default {
  name: 'EventHistory',

  components: {
    PaginationBar
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,

      loading: false,
      generating: false,
      deletingByCondition: false,
      deletingRowId: null,

      siteOptions: [],

      filters: {
        startAt: '',
        endAt: '',
        siteId: null,
        eventType: '',
        loggerName: ''
      },

      statusIconMap: {
        경보: iconDangerous,
        감지: iconDetect,
        경고: iconWarning,
        정상: iconNormal
      }
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    }
  },

  async mounted() {
    this.initDefaultDateRange()
    await this.fetchSiteOptions()
    await this.fetchEventHistoryList()
  },

  methods: {
    initDefaultDateRange() {
      const now = new Date()
      const before = new Date(now)
      before.setDate(before.getDate() - 7)

      this.filters.startAt = this.formatDateTimeLocal(before)
      this.filters.endAt = this.formatDateTimeLocal(now)
    },

    createRowDrawingState() {
      return {
        drawingImageUrl: '',
        drawingLoggerItems: [],
        drawingNaturalWidth: 0,
        drawingNaturalHeight: 0,
        drawingRenderWidth: 0,
        drawingRenderHeight: 0
      }
    },

    async fetchSiteOptions() {
      try {
        const response = await api.get('/app/site/list', {
          params: {
            deleted: false,
            orderBy: 'siteNameAsc'
          }
        })

        const result = response?.data?.result
        this.siteOptions = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('현장 목록 조회 실패', error)
        this.siteOptions = []
      }
    },

    async onChangeSite() {
      this.currentPage = 0
    },

    async fetchEventHistoryList() {
      this.loading = true

      try {
        const response = await api.get('/app/eventHistory/list', {
          params: {
            page: this.currentPage,
            size: this.size,
            orderBy: 'occurredAtDesc',
            startAt: this.formatApiDateTime(this.filters.startAt),
            endAt: this.formatApiDateTime(this.filters.endAt),
            siteId: this.filters.siteId || undefined,
            eventType: this.filters.eventType || undefined,
            loggerName: this.filters.loggerName || undefined
          }
        })

        const result = response?.data?.result
        const content = Array.isArray(result?.content) ? result.content : []

        this.tableRows = content.map(row => ({
          ...row,
          expanded: false,
          ...this.createRowDrawingState()
        }))

        this.totalCount = Number(result?.totalElements ?? 0)
        this.totalPages = Number(result?.totalPages ?? 0)
        this.currentPage = Number(result?.number ?? this.currentPage)

        if (this.currentPage > 0 && this.tableRows.length === 0) {
          this.currentPage = this.currentPage - 1
          await this.fetchEventHistoryList()
          return
        }
      } catch (error) {
        console.error('이벤트 이력 목록 조회 실패', error)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    async fetchSiteDrawingAndLoggers(siteId) {
      if (!siteId) {
        return {
          drawingImageUrl: '',
          drawingLoggerItems: []
        }
      }

      try {
        const res = await api.get('/app/site/step5/detail', {
          params: { siteId }
        })

        const result = res?.data?.result || {}
        const drawingAttachFileNo = result?.drawingAttachFileNo ?? null

        return {
          drawingImageUrl: drawingAttachFileNo
            ? `/app/file/image/${drawingAttachFileNo}`
            : '',
          drawingLoggerItems: Array.isArray(result?.loggerItems)
            ? result.loggerItems
            : []
        }
      } catch (error) {
        console.error('현장 도면/로거 조회 실패', error)
        return {
          drawingImageUrl: '',
          drawingLoggerItems: []
        }
      }
    },

    async toggleRow(id) {
      const nextRows = this.tableRows.map(row => ({
        ...row,
        expanded: row.id === id ? !row.expanded : false
      }))

      this.tableRows = nextRows

      const target = this.tableRows.find(row => row.id === id)
      if (!target || !target.expanded) return

      if (!target.siteId) {
        console.warn('siteId 없음 - 도면 조회 불가', target)
        return
      }

      if (!target.drawingImageUrl) {
        const { drawingImageUrl, drawingLoggerItems } =
          await this.fetchSiteDrawingAndLoggers(target.siteId)

        this.tableRows = this.tableRows.map(row => {
          if (row.id !== id) return row

          return {
            ...row,
            drawingImageUrl,
            drawingLoggerItems,
            drawingNaturalWidth: 0,
            drawingNaturalHeight: 0,
            drawingRenderWidth: 0,
            drawingRenderHeight: 0
          }
        })
      }
    },

    onDrawingImageLoad(rowId, event) {
      const img = event?.target
      if (!img) return

      const naturalWidth = img.naturalWidth || 0
      const naturalHeight = img.naturalHeight || 0

      this.tableRows = this.tableRows.map(row => {
        if (row.id !== rowId) return row

        const scale = this.getDrawingScaleBySize(naturalWidth, naturalHeight)

        return {
          ...row,
          drawingNaturalWidth: naturalWidth,
          drawingNaturalHeight: naturalHeight,
          drawingRenderWidth: Math.round(naturalWidth * scale),
          drawingRenderHeight: Math.round(naturalHeight * scale)
        }
      })
    },

    getDrawingScaleBySize(naturalWidth, naturalHeight) {
      if (!naturalWidth || !naturalHeight) return 1

      const maxWidth = 520
      const maxHeight = 520

      const scaleX = maxWidth / naturalWidth
      const scaleY = maxHeight / naturalHeight

      return Math.min(scaleX, scaleY, 1)
    },

    getDrawingScale(row) {
      return this.getDrawingScaleBySize(row?.drawingNaturalWidth, row?.drawingNaturalHeight)
    },

    getDrawingImageBoxStyle(row) {
      return {
        width: `${row?.drawingRenderWidth || 0}px`,
        height: `${row?.drawingRenderHeight || 0}px`
      }
    },

    normalizeServerPoint(value, fullSize) {
      const num = Number(value ?? 0)

      if (!Number.isFinite(num)) return 0

      if (num >= 0 && num <= 100) {
        return Math.round((num / 100) * fullSize)
      }

      return Math.round(num)
    },

    getDrawingLoggerKey(logger) {
      return (
        logger?.sensorLoggerSiteId ||
        logger?.sensorLoggerId ||
        logger?.loggerName ||
        logger?.name ||
        `${logger?.positionX || 0}_${logger?.positionY || 0}`
      )
    },

    resolveDrawingLoggerName(logger) {
      return (
        logger?.partName ||
        logger?.loggerName ||
        logger?.name ||
        logger?.type ||
        logger?.lineNumber ||
        logger?.line_number ||
        '-'
      )
    },

    resolveLoggerName(row) {
      return (
        row.loggerName ||
        row.sensorLoggerName ||
        row.sensor_logger_name ||
        row.logger ||
        row.sensorName ||
        '-'
      )
    },

    normalizeName(value) {
      return String(value || '')
        .replace(/\s+/g, '')
        .replace(/로거명/g, '')
        .replace(/로거/g, '')
        .replace(/생성/g, '')
        .replace(/센서/g, '')
        .replace(/측정/g, '')
        .replace(/_/g, '')
        .replace(/-/g, '')
        .trim()
        .toUpperCase()
    },

    resolveRowSensorLoggerId(row) {
      return Number(
        row?.sensorLoggerId ??
        row?.sensor_logger_id ??
        row?.sensorLogger?.id ??
        row?.loggerId ??
        row?.logger_id ??
        0
      )
    },

    resolveDrawingSensorLoggerId(logger) {
      return Number(
        logger?.sensorLoggerId ??
        logger?.sensor_logger_id ??
        logger?.sensorLogger?.id ??
        0
      )
    },

    isEventTargetLogger(row, logger) {
      const rowLoggerId = this.resolveRowSensorLoggerId(row)
      const drawingLoggerId = this.resolveDrawingSensorLoggerId(logger)

      return rowLoggerId > 0 && drawingLoggerId > 0 && rowLoggerId === drawingLoggerId
    },

    findMatchedDrawingLogger(row) {
      const list = Array.isArray(row?.drawingLoggerItems) ? row.drawingLoggerItems : []
      return list.find(logger => this.isEventTargetLogger(row, logger)) || null
    },

    getEventTargetLoggers(row) {
      const matched = this.findMatchedDrawingLogger(row)
      return matched ? [matched] : []
    },

    resolveCompanyName(row) {
      return row.companyName || row.agencyName || row.clientName || '-'
    },

    getLoggerStatusLabel(row, logger) {
      if (this.isEventTargetLogger(row, logger)) {
        return row?.eventType || '정상'
      }
      return '정상'
    },

    getLoggerStatusClass(row, logger) {
      const status = this.getLoggerStatusLabel(row, logger)

      if (status === '경보') return 'is-dangerous'
      if (status === '감지') return 'is-detect'
      if (status === '경고') return 'is-warning'
      return 'is-normal'
    },

    getLoggerIconSrc(row, logger) {
      const status = this.getLoggerStatusLabel(row, logger)
      return this.statusIconMap[status] || this.statusIconMap['정상']
    },

    getLoggerMarkerStyle(row, logger) {
      if (!row?.drawingNaturalWidth || !row?.drawingNaturalHeight) {
        return {
          display: 'none'
        }
      }

      const naturalX = this.normalizeServerPoint(logger.positionX, row.drawingNaturalWidth)
      const naturalY = this.normalizeServerPoint(logger.positionY, row.drawingNaturalHeight)

      const scale = this.getDrawingScale(row)

      return {
        left: `${naturalX * scale}px`,
        top: `${naturalY * scale}px`
      }
    },

    async onClickGenerate() {
      if (!confirm('이벤트 이력을 생성하시겠습니까?')) {
        return
      }

      this.generating = true

      try {
        const response = await api.get('/app/eventHistory/generate')
        console.log('event generate response =', response?.data)
        alert('이벤트 이력 생성이 완료되었습니다.')
        this.currentPage = 0
        await this.fetchEventHistoryList()
      } catch (error) {
        console.error('이벤트 이력 생성 실패', error)
        alert('이벤트 이력 생성 중 오류가 발생했습니다.')
      } finally {
        this.generating = false
      }
    },

    async onDeleteRow(row) {
      if (!row?.id) {
        alert('삭제할 이벤트 ID가 없습니다.')
        return
      }

      const firstOk = confirm(
        `[${row.id}] 이벤트 이력을 삭제하시겠습니까?\n\n삭제 후 목록에서 표시되지 않습니다.`
      )
      if (!firstOk) return

      const secondOk = confirm(
        `[${row.id}] 이벤트 이력을 정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.`
      )
      if (!secondOk) return

      this.deletingRowId = row.id

      try {
        await api.post('/app/eventHistory/delete', {
          id: row.id
        })

        alert('이벤트 이력 삭제 완료')
        await this.fetchEventHistoryList()
      } catch (error) {
        console.error('이벤트 이력 단건 삭제 실패', error)
        alert(error?.response?.data?.message || '이벤트 이력 삭제 중 오류가 발생했습니다.')
      } finally {
        this.deletingRowId = null
      }
    },

    async onDeleteByCondition() {
      if (!this.filters.siteId) {
        alert('현장을 선택해주세요.')
        return
      }

      if (!this.filters.startAt || !this.filters.endAt) {
        alert('시작일시와 종료일시는 필수입니다.')
        return
      }

      const selectedSite = this.siteOptions.find(
        item => Number(item.id) === Number(this.filters.siteId)
      )

      const siteText = selectedSite?.name || `siteId=${this.filters.siteId}`

      const firstOk = confirm(
        `선택한 조건의 이벤트 이력을 삭제하시겠습니까?\n\n` +
        `현장: ${siteText}\n` +
        `기간: ${this.filters.startAt} ~ ${this.filters.endAt}\n` +
        `이벤트 유형: ${this.filters.eventType || '전체'}\n` +
        `로거명: ${this.filters.loggerName || '전체'}`
      )
      if (!firstOk) return

      const secondOk = confirm(
        `정말로 조건 삭제를 진행하시겠습니까?\n\n삭제 시 복구가 불가합니다.`
      )
      if (!secondOk) return

      this.deletingByCondition = true

      try {
        const response = await api.post('/app/eventHistory/deleteByCondition', {
          siteId: this.filters.siteId,
          startAt: this.formatApiDateTime(this.filters.startAt),
          endAt: this.formatApiDateTime(this.filters.endAt),
          eventType: this.filters.eventType || '',
          loggerName: this.filters.loggerName || '',
          siteName: '',
          sensorName: ''
        })

        const deletedCount = Number(response?.data?.result?.deletedCount ?? 0)

        alert(`조건 이벤트 이력 삭제 완료 (${deletedCount}건)`)
        this.currentPage = 0
        await this.fetchEventHistoryList()
      } catch (error) {
        console.error('조건 이벤트 삭제 실패', error)
        alert(error?.response?.data?.message || '조건 이벤트 삭제 중 오류가 발생했습니다.')
      } finally {
        this.deletingByCondition = false
      }
    },

    async onSearch() {
      this.currentPage = 0
      await this.fetchEventHistoryList()
    },

    async onResetFilters() {
      this.filters.siteId = null
      this.filters.eventType = ''
      this.filters.loggerName = ''
      this.initDefaultDateRange()

      this.currentPage = 0
      await this.fetchEventHistoryList()
    },

    async onChangePage(page) {
      this.currentPage = page
      await this.fetchEventHistoryList()
    },

    getEventTypeClass(eventType) {
      if (eventType === '경보') return 'is-alarm'
      if (eventType === '감지') return 'is-detect'
      if (eventType === '경고') return 'is-warning'
      return ''
    },

    inferUnit(row) {
      if (row?.measureMm !== null && row?.measureMm !== undefined) return 'mm'
      return null
    },

    formatNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return num.toFixed(4)
    },

    formatDetailValue(value, unit) {
      if (value === null || value === undefined || value === '') return '-'

      const num = Number(value)
      if (Number.isFinite(num)) {
        return unit ? `${num.toFixed(4)} ${unit}` : num.toFixed(4)
      }

      return String(value)
    },

    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yy = String(date.getFullYear()).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },

    formatDateTimeLocal(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''

      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')

      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
    },

    formatApiDateTime(value) {
      if (!value) return undefined

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return undefined

      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    }
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-pagination.css";

/* =========================
   FILTER
========================= */
.filter-section {
  margin-bottom: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  box-sizing: border-box;
}

/* 시작일시 / 종료일시 / 현장 / 이벤트유형 / 로거명 / 검색 / 초기화 */
.filter-grid {
  display: grid;
  grid-template-columns:
    minmax(180px, 220px)
    minmax(180px, 220px)
    minmax(180px, 1fr)
    minmax(150px, 180px)
    minmax(180px, 220px)
    120px
    120px;
  gap: 12px;
  align-items: end;
}

.filter-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  line-height: 1.2;
}

.filter-input {
  width: 100%;
  height: 42px;
  box-sizing: border-box;
}

.filter-actions {
  display: contents;
  min-width: 0;
  overflow: visible;
}

.filter-actions .btn {
  width: 88px;
  min-width: 88px;
  height: 42px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* =========================
   TABLE
========================= */
.table-section {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.data-table {
  width: 100%;
  min-width: 1100px;
  table-layout: fixed;
}

.data-table th,
.data-table td {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.45;
  vertical-align: middle;
}

.td-ellipsis {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.col-toggle { width: 54px; }
.col-date { width: 170px; }
.col-site { width: 170px; }
.col-logger { width: 150px; }
.col-sensor { width: 150px; }
.col-location { width: 140px; }
.col-type { width: 110px; }
.col-number { width: 110px; }
.col-status { width: 180px; }
.col-action { width: 96px; }

.table-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.table-row:hover td {
  background: #f8fafc;
}

.table-row.expanded td {
  background: #f1f5f9;
}

.td-toggle {
  text-align: center;
}

.table-row td:last-child {
  text-align: center;
}

.table-row td:last-child .btn {
  min-width: 56px;
}

/* =========================
   EXPAND BUTTON
========================= */
.expand-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.expand-btn:hover {
  background: #f3f4f6;
}

/* =========================
   DETAIL
========================= */
.detail-row td {
  padding: 0;
}

.detail-panel {
  display: grid;
  grid-template-columns: 420px minmax(0, 1fr);
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
}

.detail-left,
.detail-right {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  min-width: 0;
}

.detail-title,
.detail-map-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.detail-grid {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 8px 12px;
}

.detail-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  color: #111827;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.detail-value.is-emphasis {
  color: #dc2626;
  font-weight: 700;
}

.detail-value.is-strong {
  color: #111827;
  font-weight: 700;
}

/* =========================
   DRAWING MAP
========================= */
.detail-map-box {
  width: 100%;
  min-height: 560px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  border-radius: 8px;
  overflow: auto;
  padding: 18px;
  box-sizing: border-box;
}

.detail-map-stage {
  width: 100%;
  min-width: max-content;
  min-height: 100%;
  text-align: center;
}

.detail-map-image-wrap {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

.detail-map-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: fill;
  user-select: none;
  pointer-events: none;
}

.detail-map-measure-image {
  opacity: 0;
  pointer-events: none;
  position: absolute;
}

.detail-map-empty {
  width: 100%;
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
}

/* =========================
   LOGGER MARKER / TOOLTIP
========================= */
.logger-marker {
  position: absolute;
  z-index: 20;
  transform: translate(-50%, -100%);
  pointer-events: none;
}

.logger-icon-wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  pointer-events: auto;
}

.logger-status-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
}

.logger-tooltip {
  position: absolute;
  left: 36px;
  top: -6px;
  min-width: 220px;
  max-width: 280px;
  padding: 10px 12px;
  border: 1px solid #ef4444;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  line-height: 1.45;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.14);
  opacity: 0;
  visibility: hidden;
  transform: translateY(4px);
  transition: opacity 0.16s ease, transform 0.16s ease;
  pointer-events: none;
  z-index: 30;
  white-space: normal;
}

.logger-icon-wrap.is-normal .logger-tooltip { border-color: #94a3b8; }
.logger-icon-wrap.is-warning .logger-tooltip { border-color: #f59e0b; }
.logger-icon-wrap.is-detect .logger-tooltip { border-color: #a855f7; }
.logger-icon-wrap.is-dangerous .logger-tooltip { border-color: #ef4444; }

.logger-icon-wrap:hover .logger-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tooltip-row + .tooltip-row {
  margin-top: 4px;
}

.tooltip-label {
  font-weight: 700;
  margin-right: 4px;
}

.tooltip-value {
  color: #111827;
}

/* =========================
   EVENT PILL
========================= */
.event-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.event-pill.is-warning {
  background: #fff7e6;
  color: #d97706;
}

.event-pill.is-detect {
  background: #eef4ff;
  color: #2563eb;
}

.event-pill.is-alarm {
  background: #fef2f2;
  color: #dc2626;
}

/* =========================
   PAGINATION / EMPTY
========================= */
.pagination-area {
  padding-top: 16px;
}

.table-empty {
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

/* =========================
   RESPONSIVE
========================= */
@media (max-width: 1600px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-actions {
    display: flex;
    flex-direction: row;
    align-items: end;
    justify-content: flex-end;
    gap: 8px;
    grid-column: 3 / 4;
  }

  .filter-actions .btn {
    width: 88px;
    min-width: 88px;
  }
}

@media (max-width: 1200px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    grid-column: 1 / -1;
  }

  .detail-panel {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    grid-column: auto;
  }

  .filter-actions .btn {
    width: 88px;
    min-width: 88px;
  }
}

/* =========================
   BUTTON HOVER
========================= */
.btn:hover:not(:disabled) {
  position: relative;
  z-index: 2;
}

.header-buttons .btn:hover:not(:disabled),
.filter-actions .btn:hover:not(:disabled),
.table-row td:last-child .btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
}

.header-buttons .btn.danger:hover:not(:disabled),
.table-row td:last-child .btn.danger:hover:not(:disabled) {
  box-shadow: 0 10px 22px rgba(239, 68, 68, 0.32);
}

.header-buttons .btn.primary:hover:not(:disabled),
.filter-actions .btn.primary:hover:not(:disabled) {
  box-shadow: 0 10px 22px rgba(224, 137, 36, 0.34);
}
</style>